/**
 * Canonical API Client for UnityLove
 *
 * Goals:
 * - Single baseURL source (prevents fork drift)
 * - Auto-attaches JWT token
 * - Consistent 401 session-expired handling (delegates to App.js global handler)
 * - 403 Elite gating (NO logout)
 * - Centralized error handling
 *
 * Usage:
 *   import apiClient from '../api/client';
 *   // or: import { apiClient } from '../api';
 *
 *   apiClient.get('/lounge/status')  // Note: no /api prefix needed
 */

import axios from 'axios';

const BACKEND_URL = window.location.origin;
const DEFAULT_TIMEOUT = 15000;

const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: DEFAULT_TIMEOUT,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - inject token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401/403
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config || {};

    // 401: session expired → delegate to global handler (single source of truth)
    if (status === 401) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch {}

      window.__UL_handleSessionExpired?.({
        source: 'apiClient',
        url: originalRequest.url,
        status,
      });

      // Fallback if handler missing (shouldn't happen)
      if (!window.__UL_handleSessionExpired && window.location.pathname !== '/auth') {
        window.location.href = '/auth?reason=session_expired';
      }

      return Promise.reject(error);
    }

    // 403: Elite-required gating (NO logout)
    if (status === 403) {
      const headers = error.response?.headers || {};
      const eliteRequired =
        headers['x-elite-required'] ||
        headers['X-Elite-Required'] ||
        (typeof headers.get === 'function' ? headers.get('x-elite-required') : null);

      if (eliteRequired === 'true' || eliteRequired === true) {
        console.warn('[API] 403 Elite-required - showing upgrade prompt');

        import('../utils/eliteGate')
          .then(({ triggerEliteUpgradeModal }) => {
            const url = originalRequest.url || '';
            let featureName = 'this feature';
            if (url.includes('who-liked') || url.includes('matches/who')) featureName = 'Who Liked Me';
            else if (url.includes('elite')) featureName = 'Elite features';
            triggerEliteUpgradeModal(featureName);
          })
          .catch(() => {
            if (window.location.pathname !== '/shop') {
              window.location.href = '/shop?focus=elite&reason=required';
            }
          });

        return Promise.reject(error);
      }

      // Regular 403
      const silentEndpoints = ['/lounge/status', '/chat/unread-count', '/presence/heartbeat'];
      if (!silentEndpoints.some((ep) => (originalRequest.url || '').includes(ep))) {
        console.warn('[API] 403 Forbidden:', originalRequest.url);
      }
    }

    return Promise.reject(error);
  }
);

// Helpers
export const isAuthenticated = () => !!localStorage.getItem('token');
export const getToken = () => localStorage.getItem('token');

export default apiClient;

export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};
